import { useMemo, useState } from 'react';
import {
  DEFAULT_CONTENT_LOCALE,
  isBlockSuppressed,
  type CourseBlock,
} from '@course-authoring/shared-types';
import {
  AskEmaWidget,
  AudioControlsProvider,
  CollapsibleNavLayout,
  ContentFooter,
  shouldShowLessonLanguageFallback,
  LanguageUnavailableBanner,
  PrivacyNotice,
  SaveExitBar,
  UserToolbar,
  WelcomeModal,
  useBlockPacing,
} from '@course-authoring/ui-blocks';
import { ScopedI18nProvider } from '@course-authoring/i18n';
import type { LessonEditorData } from '../../api/courseApi';
import {
  PreviewCanvas,
  PreviewNavigationSidebar,
} from '../../components';
import { LessonPreviewCanvas } from '../../components/PreviewCanvas';
import { ResolvedAccountConfigProvider } from '../../config/ResolvedAccountConfigProvider';
import { useAskExpertPreviewOverride } from '../../hooks/useClientContext';
import { useEditorStore, type LessonMeta } from '../../store/editorStore';
import { ResolvedThemeProvider } from '../../theme/ResolvedThemeProvider';
import { lessonEditorPageStyles as styles } from './lessonEditorPageStyles';

type LessonContentData = Omit<LessonEditorData, 'publishState' | 'deleteEligibility'>;

interface LessonEditorPreviewProps {
  lessonId: string;
  lessonData: LessonContentData | undefined;
  lessonMeta: LessonMeta;
  selectedLocale: string;
  onLanguageSelect: (locale: string) => void;
  shouldShowWelcome: boolean;
  shouldForcePrivacy: boolean;
  shouldShowSaveExitBar: boolean;
}

interface LessonPreviewOverlaysProps {
  forcePrivacy: boolean;
  showWelcome: boolean;
  welcomeTitle: string;
  enableAskExpertOverride?: boolean;
  onWelcomeDismiss: () => void;
}

// Privacy notice is a legal acknowledgment gate, not a themeable account
// setting — preview has no logged-in learner to hold an acknowledgment
// record, so it stays URL-param gated only (forcePrivacy = showPrivacy=1).
function LessonPreviewOverlays({
  forcePrivacy,
  showWelcome,
  welcomeTitle,
  enableAskExpertOverride,
  onWelcomeDismiss,
}: LessonPreviewOverlaysProps) {
  const [privacyDismissed, setPrivacyDismissed] = useState(false);
  const privacyVisible = forcePrivacy && !privacyDismissed;

  return (
    <>
      {privacyVisible && <PrivacyNotice onDismiss={() => setPrivacyDismissed(true)} />}
      {!privacyVisible && showWelcome && (
        <WelcomeModal
          courseTitle={welcomeTitle}
          enableAskExpert={enableAskExpertOverride}
          onDismiss={onWelcomeDismiss}
        />
      )}
    </>
  );
}

function LessonEditorPreviewContent({
  lessonId,
  lessonData,
  lessonMeta,
  selectedLocale,
  onLanguageSelect,
  shouldShowWelcome,
  shouldForcePrivacy,
  shouldShowSaveExitBar,
}: LessonEditorPreviewProps) {
  const previewBlockCompletion = useEditorStore((state) => state.previewBlockCompletion);
  const markPreviewBlockComplete = useEditorStore((state) => state.markPreviewBlockComplete);
  const markPreviewEngaged = useEditorStore((state) => state.markPreviewEngaged);
  const previewTargetBlockId = useEditorStore((state) => state.previewTargetBlockId);
  const setPreviewTargetBlockId = useEditorStore((state) => state.setPreviewTargetBlockId);
  const editorBlocks = useEditorStore((state) => state.blocks);
  const editorBlockOrder = useEditorStore((state) => state.blockOrder);
  const pacing = useBlockPacing(JSON.stringify([lessonId, editorBlockOrder]));
  const askExpertPreviewOverride = useAskExpertPreviewOverride();
  const [welcomeDismissed, setWelcomeDismissed] = useState(!shouldShowWelcome);
  // NA-408 "Show Block IDs": ephemeral, resets every mount — never persisted.
  // No course context here (standalone lesson editor) — the id string omits
  // the C<courseId> segment entirely.
  const [showBlockIds, setShowBlockIds] = useState(false);

  const localizedPreviewBlocks = useMemo(() => {
    if (!lessonData || selectedLocale === DEFAULT_CONTENT_LOCALE) return null;
    return lessonData.blockOrder
      .map((blockId) => lessonData.blocks[blockId])
      .filter((block): block is CourseBlock => block !== undefined);
  }, [lessonData, selectedLocale]);
  const coverageMeta = lessonData?.lessonMeta ?? lessonMeta;
  const showLanguageFallback = shouldShowLessonLanguageFallback({
    selectedLocale,
    defaultLocale: coverageMeta.defaultLocale,
    fullyTranslatedLocales: coverageMeta.fullyTranslatedLocales,
    availableLocales: coverageMeta.availableLocales,
  });
  const languageNotice = showLanguageFallback
    ? <LanguageUnavailableBanner />
    : undefined;
  const defaultPreviewBlocks = useMemo(
    () => editorBlockOrder
      .map((blockId) => editorBlocks[blockId])
      .filter((block): block is CourseBlock => block !== undefined)
      .filter((block) => !isBlockSuppressed(block)),
    [editorBlockOrder, editorBlocks],
  );
  const narrationBlocks = localizedPreviewBlocks ?? defaultPreviewBlocks;

  return (
    <ScopedI18nProvider locale={selectedLocale}>
      <div style={styles.mainLayout} lang={selectedLocale}>
        <ResolvedThemeProvider>
          <AudioControlsProvider
            lessonId={lessonId || null}
            blocks={narrationBlocks}
            narration={lessonData?.narration}
          >
            <div style={styles.previewLayout}>
              {shouldShowSaveExitBar && <SaveExitBar onSaveExit={() => undefined} />}
              <CollapsibleNavLayout
                renderNav={() => <PreviewNavigationSidebar />}
                renderToolbar={(variant) => (
                  <UserToolbar
                    variant={variant}
                    selectedLocale={selectedLocale}
                    onLanguageSelect={onLanguageSelect}
                    isPreview
                    showBlockIds={showBlockIds}
                    onShowBlockIdsChange={setShowBlockIds}
                  />
                )}
              >
                <LessonPreviewOverlays
                  forcePrivacy={shouldForcePrivacy}
                  showWelcome={shouldShowWelcome && !welcomeDismissed}
                  welcomeTitle={lessonMeta.title || 'Untitled Lesson'}
                  enableAskExpertOverride={askExpertPreviewOverride}
                  onWelcomeDismiss={() => setWelcomeDismissed(true)}
                />
                {localizedPreviewBlocks ? (
                  <LessonPreviewCanvas
                    pacing={pacing}
                    key={selectedLocale}
                    blocks={localizedPreviewBlocks}
                    completionStatus={previewBlockCompletion}
                    onBlockComplete={markPreviewBlockComplete}
                    enableCompletionTracking
                    enableSuccessAnimations
                    enableBlockReveal={false}
                    lessonTitle={lessonData?.lessonMeta.title}
                    lessonThemeColor={lessonData?.lessonMeta.themeColor}
                    currentLocale={selectedLocale}
                    lessonNumericId={lessonMeta.numericId}
                    lessonLocale={selectedLocale}
                    showBlockIds={showBlockIds}
                    topContent={languageNotice}
                    previewTargetBlockId={previewTargetBlockId}
                    onPreviewTargetHandled={() => setPreviewTargetBlockId(null)}
                    footerContent={<ContentFooter />}
                    onEngage={markPreviewEngaged}
                  />
                ) : (
                  <PreviewCanvas
                    pacing={pacing}
                    key={selectedLocale}
                    currentLocale={selectedLocale}
                    lessonNumericId={lessonMeta.numericId}
                    lessonLocale={selectedLocale}
                    showBlockIds={showBlockIds}
                    topContent={languageNotice}
                    footerContent={<ContentFooter />}
                  />
                )}
                <AskEmaWidget isPreview askExpertEnabled={askExpertPreviewOverride} />
              </CollapsibleNavLayout>
            </div>
          </AudioControlsProvider>
        </ResolvedThemeProvider>
      </div>
    </ScopedI18nProvider>
  );
}

/** Keep the launch owner below account resolution and above locale-keyed canvases. */
export function LessonEditorPreview(props: LessonEditorPreviewProps) {
  return (
    <ResolvedAccountConfigProvider>
      <LessonEditorPreviewContent {...props} />
    </ResolvedAccountConfigProvider>
  );
}
